<script setup>
// SiteUp: seletor de "modo de exibição" das conversas (estilo WhatsApp). O modo
// é persistido por usuário no uiSettings (conversation_view_mode) e aplicado no
// ChatList (conversationList → conversationViewComparator), valendo em todas as
// visões. Padrão = "Atividade mais recente".
// Duas formas: `dropdown` (ícone + painel teleportado) e `inline` (lista dentro
// de um painel que já está aberto, como a seção Ordenar do menu Atalhos).
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { DEFAULT_CONVERSATION_VIEW_MODE } from 'dashboard/store/modules/conversations/helpers';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { VIEW_MODES, ehModoValido } from './conversationSortModes';

const props = defineProps({
  variant: {
    type: String,
    default: 'dropdown',
    validator: value => ['dropdown', 'inline'].includes(value),
  },
  teleportTarget: {
    type: String,
    default: '#conversation-header-menu-target',
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();

const isOpen = ref(false);
const rootRef = ref(null);
const panelRef = ref(null);

const modeLabel = mode =>
  t(`CHAT_LIST.HEADER_FILTERS.SORT_MODES.${mode.i18nKey}`);

// Modo salvo que não está mais na lista (os cinco removidos em 26/08) cai no
// padrão: senão a pessoa fica presa num modo que o menu não mostra e não
// consegue mais trocar por ali.
const currentMode = computed(() => {
  const saved = uiSettings.value?.conversation_view_mode;
  return ehModoValido(saved) ? saved : DEFAULT_CONVERSATION_VIEW_MODE;
});

const currentLabel = computed(() =>
  modeLabel(
    VIEW_MODES.find(m => m.value === currentMode.value) || VIEW_MODES[0]
  )
);
const tooltipLabel = computed(
  () => `${t('CHAT_LIST.SORT_TOOLTIP_LABEL')}: ${currentLabel.value}`
);

// Destaca o ícone quando o modo difere do padrão (sinaliza configuração ativa).
const isCustomized = computed(
  () => currentMode.value !== DEFAULT_CONVERSATION_VIEW_MODE
);

const select = value => {
  updateUISettings({ conversation_view_mode: value });
  isOpen.value = false;
};

const close = () => {
  isOpen.value = false;
};
const onDocClick = e => {
  if (!isOpen.value) return;
  if (rootRef.value?.contains(e.target)) return;
  if (panelRef.value?.contains(e.target)) return;
  close();
};
onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));
</script>

<template>
  <div
    v-if="props.variant === 'inline'"
    class="flex min-w-0 flex-col gap-0.5"
    role="radiogroup"
    :aria-label="t('CHAT_LIST.SORT_TOOLTIP_LABEL')"
  >
    <button
      v-for="mode in VIEW_MODES"
      :key="mode.value"
      type="button"
      role="radio"
      :data-testid="`atalhos-sort-${mode.value}`"
      :aria-checked="mode.value === currentMode"
      class="flex min-w-0 items-center gap-2 rounded-xl px-2 py-2 text-left text-xs transition-colors"
      :class="
        mode.value === currentMode
          ? 'bg-n-brand/10 font-medium text-n-blue-11'
          : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
      "
      @click="select(mode.value)"
    >
      <span
        class="size-3.5 shrink-0"
        :class="mode.icon"
        :data-icon="mode.icon"
        aria-hidden="true"
      />
      <span class="flex-1 truncate">{{ modeLabel(mode) }}</span>
      <span
        v-if="mode.value === currentMode"
        class="i-ri-check-line size-3.5 shrink-0 text-n-blue-11"
        aria-hidden="true"
      />
    </button>
  </div>
  <div v-else ref="rootRef" class="relative">
    <NextButton
      v-tooltip.bottom="tooltipLabel"
      icon="i-ri-swap-line"
      :color="isCustomized ? 'teal' : 'slate'"
      :aria-label="tooltipLabel"
      :aria-expanded="isOpen"
      aria-controls="conversation-sort-menu"
      size="xs"
      faded
      @click="isOpen = !isOpen"
    />
    <Teleport v-if="isOpen" :to="props.teleportTarget">
      <div
        id="conversation-sort-menu"
        ref="panelRef"
        class="pointer-events-auto flex w-full min-w-0 flex-col gap-0.5 rounded-2xl border border-n-weak bg-n-solid-1 p-1.5 outline outline-1 outline-n-weak"
        @keydown.esc="close"
      >
        <p class="px-2 pt-1 pb-1.5 text-xs font-medium text-n-slate-10">
          {{ t('CHAT_LIST.SORT_TOOLTIP_LABEL') }}
        </p>
        <button
          v-for="mode in VIEW_MODES"
          :key="mode.value"
          type="button"
          class="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm text-n-slate-12 hover:bg-n-alpha-2 text-left"
          :class="{ 'bg-n-alpha-2': mode.value === currentMode }"
          @click="select(mode.value)"
        >
          <span :class="mode.icon" class="size-4 shrink-0 text-n-slate-11" />
          <span class="flex-1 truncate">{{ modeLabel(mode) }}</span>
          <span
            v-if="mode.value === currentMode"
            class="i-ri-check-line size-4 shrink-0 text-n-teal-10"
          />
        </button>
      </div>
    </Teleport>
  </div>
</template>
